import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import RecordsPage from "@/pages/RecordsPage";
import UsersPage from "@/pages/UsersPage";
import RegionsPage from "@/pages/RegionsPage";
import ImportExportPage from "@/pages/ImportExportPage";
import AuditPage from "@/pages/AuditPage";
import TechniciansPage from "@/pages/TechniciansPage";
import WorkOrderLookupPage from "@/pages/WorkOrderLookupPage";
import RequestsPage from "@/pages/RequestsPage";
import InventoryPage from "@/pages/InventoryPage";
import PartsPage from "@/pages/PartsPage";
import FinancePage from "@/pages/FinancePage";
import SuppliersPage from "@/pages/SuppliersPage";
import CustomersPage from "@/pages/CustomersPage";
import PricesPage from "@/pages/PricesPage";
import ReturnApprovalsPage from "@/pages/ReturnApprovalsPage";
import FaultyPartsPage from "@/pages/FaultyPartsPage";
import PackingListPage from "@/pages/PackingListPage";
import DeliveryOrdersPage from "@/pages/DeliveryOrdersPage";
import PurchaseOrdersPage from "@/pages/PurchaseOrdersPage";
import QuotationsPage from "@/pages/QuotationsPage";

function Protected({ children, needs, roles, denyRoles }) {
  const { user, ready, can, hasRole } = useAuth();
  if (!ready) return <div className="p-10 text-sm text-neutral-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (denyRoles && hasRole(...denyRoles)) return <Navigate to="/" replace />;
  if (roles && !hasRole(...roles)) return <Navigate to="/" replace />;
  if (needs && !needs.some((p) => can(p))) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors closeButton />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/"
            element={
              <Protected>
                <Layout />
              </Protected>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="records" element={<RecordsPage />} />
            <Route path="lookup" element={<WorkOrderLookupPage />} />
            <Route path="requests" element={<RequestsPage />} />
            <Route path="return-approvals" element={<ReturnApprovalsPage />} />
            <Route path="faulty-parts" element={<FaultyPartsPage />} />
            <Route
              path="packing-lists"
              element={
                <Protected roles={["storekeeper", "manager", "supervisor", "admin"]}>
                  <PackingListPage />
                </Protected>
              }
            />
            <Route
              path="delivery-orders"
              element={
                <Protected roles={["storekeeper", "manager", "supervisor", "admin"]}>
                  <DeliveryOrdersPage />
                </Protected>
              }
            />
            <Route
              path="purchase-orders"
              element={
                <Protected roles={["manager", "supervisor", "admin"]}>
                  <PurchaseOrdersPage />
                </Protected>
              }
            />
            <Route
              path="quotations"
              element={
                <Protected roles={["manager", "supervisor", "admin"]}>
                  <QuotationsPage />
                </Protected>
              }
            />
            <Route path="inventory" element={<InventoryPage />} />
            <Route
              path="parts"
              element={
                <Protected needs={["add_stock"]}>
                  <PartsPage />
                </Protected>
              }
            />
            <Route
              path="technicians"
              element={
                <Protected needs={["manage_users"]}>
                  <TechniciansPage />
                </Protected>
              }
            />
            <Route
              path="data"
              element={
                <Protected needs={["export_csv"]}>
                  <ImportExportPage />
                </Protected>
              }
            />
            <Route
              path="users"
              element={
                <Protected needs={["manage_users"]}>
                  <UsersPage />
                </Protected>
              }
            />
            <Route
              path="regions"
              element={
                <Protected needs={["manage_master"]}>
                  <RegionsPage />
                </Protected>
              }
            />
            <Route
              path="audit"
              element={
                <Protected needs={["view_audit"]}>
                  <AuditPage />
                </Protected>
              }
            />
            <Route
              path="finance"
              element={
                <Protected roles={["finance", "manager"]}>
                  <FinancePage />
                </Protected>
              }
            />
            <Route
              path="suppliers"
              element={
                <Protected roles={["manager"]}>
                  <SuppliersPage />
                </Protected>
              }
            />
            <Route
              path="customers"
              element={
                <Protected roles={["manager", "supervisor", "finance", "admin"]}>
                  <CustomersPage />
                </Protected>
              }
            />
            <Route
              path="prices"
              element={
                <Protected roles={["finance", "manager"]}>
                  <PricesPage />
                </Protected>
              }
            />
          </Route>
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
